import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import API_BASE_URL from "../../../components/apiconfig/api-config";
import { formatDateDDMMYY } from "../../../components/common/dateUtils";

const sectionClass =
  "bg-white border border-gray-200 p-4 mb-4 overflow-x-auto";
const thClass =
  "px-4 py-3 bg-gray-100 text-gray-600 font-bold text-sm text-left border-b border-gray-200";
const tdClass = "px-4 py-3 text-sm text-gray-700 border-b border-gray-100";
const zebraClass = (i) => (i % 2 === 0 ? "bg-white" : "bg-gray-50");

// Using the common formatDateDDMMYY function from dateUtils

const MasterReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedOrders, setExpandedOrders] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [expandedSalesContracts, setExpandedSalesContracts] = useState({});
  const [showWarehouseStockFlow, setShowWarehouseStockFlow] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [activeSalesTab, setActiveSalesTab] = useState('sales-contracts');
  const [selectedContractFilter, setSelectedContractFilter] = useState(null);
  const location = useLocation();

  // Get contract number from URL parameters
  const getContractFromURL = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('contract');
  };

  // Auto-load data when component mounts or URL changes
  useEffect(() => {
    const contractNo = getContractFromURL();
    if (contractNo) {
      handleSearch(contractNo);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async (contractNo) => {
    setLoading(true);
    setError("");
    setData(null);
    setShowContent(false);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/report/master-report?purchase_contract_no=${encodeURIComponent(
          contractNo
        )}`,
        {
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error("No data found");
      const result = await res.json();
      console.log('Master report data received:', result);
      console.log('Customs clearance data:', result.customsClearance);
      setData(result);
      // Add a small delay before showing content for smooth animation
      setTimeout(() => {
        setShowContent(true);
      }, 300);
    } catch (err) {
      setError("No data found or error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const toggleOrder = (orderId) => {
    setExpandedOrders((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };
  // eslint-disable-next-line no-unused-vars
  const toggleSalesContract = (scId) => {
    setExpandedSalesContracts((prev) => ({ ...prev, [scId]: !prev[scId] }));
  };
  
  const handleContractClick = (contractId) => {
    setSelectedContractFilter(contractId);
    setActiveSalesTab('delivery-orders');
  };
  
  const clearContractFilter = () => {
    setSelectedContractFilter(null);
  };

  const toggleWarehouseStockFlow = () => {
    setShowWarehouseStockFlow((prev) => !prev);
  };

  // Function to scroll to section smoothly
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white min-h-screen">
      {getContractFromURL() && data && (
        <div className={`bg-white border border-gray-200 p-6 mb-6 transition-all duration-700 ease-in-out ${showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="text-center lg:text-left">
              <h2 className="text-2xl font-bold text-gray-800 tracking-tight">
                {getContractFromURL()}
              </h2>
            </div>
            
            {/* Navigation Menu */}
            <div className="text-center lg:text-right">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Quick Navigation:</h3>
              <div className="flex flex-wrap gap-2 justify-center lg:justify-end">
                {data.contract && (
                  <button
                    onClick={() => scrollToSection('purchase-contract')}
                    className="px-3 py-1 bg-gray-100 text-[#2691cc] text-xs font-medium rounded hover:bg-gray-200 transition-colors"
                  >
                    Purchase Contract
                  </button>
                )}
                {data.importOrders && data.importOrders.length > 0 && (
                  <button
                    onClick={() => scrollToSection('import-orders')}
                    className="px-3 py-1 bg-gray-100 text-[#2691cc] text-xs font-medium rounded hover:bg-gray-200 transition-colors"
                  >
                    Vessel Info
                  </button>
                )}
                {data.insuranceReport && data.insuranceReport.length > 0 && (
                  <button
                    onClick={() => scrollToSection('insurance')}
                    className="px-3 py-1 bg-gray-100 text-[#2691cc] text-xs font-medium rounded hover:bg-gray-200 transition-colors"
                  >
                    Insurance
                  </button>
                )}
                {data.salesContracts && data.salesContracts.length > 0 && (
                  <button
                    onClick={() => scrollToSection('sales-contracts')}
                    className="px-3 py-1 bg-gray-100 text-[#2691cc] text-xs font-medium rounded hover:bg-gray-200 transition-colors"
                  >
                    Sales Contracts
                  </button>
                )}
                {data.warehouseStockFlow && data.warehouseStockFlow.length > 0 && (
                  <button
                    onClick={() => scrollToSection('warehouse')}
                    className="px-3 py-1 bg-gray-100 text-[#2691cc] text-xs font-medium rounded hover:bg-gray-200 transition-colors"
                  >
                    Warehouse
                  </button>
                )}
                {data.costBreakups && data.costBreakups.length > 0 && (
                  <button
                    onClick={() => scrollToSection('cost-breakup')}
                    className="px-3 py-1 bg-gray-100 text-[#2691cc] text-xs font-medium rounded hover:bg-gray-200 transition-colors"
                  >
                    Cost Breakup
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-8 space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-gray-600"></div>
          <p className="text-gray-600 font-medium text-sm">Loading master report...</p>
        </div>
      ) : error ? (
        <div className={`bg-red-50 border border-red-200 p-4 mb-4 transition-all duration-700 ease-in-out ${showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-red-800">
                Error loading data
              </h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      ) : data ? (
        <div className={`transition-all duration-700 ease-in-out ${showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
            {/* Purchase Contract Details */}
            {data.contract && (
              <section id="purchase-contract" className={`${sectionClass} transition-all duration-700 ease-in-out delay-100 ${showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-lg font-bold text-gray-800">
                    Purchase Contract Details
                  </h3>
                </div>
                <div className="min-w-max">
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
                  <div className="bg-gray-50 p-3 border border-gray-200">
                    <div className="text-sm font-bold text-gray-600 tracking-wide mb-1">
                      Contract No
                    </div>
                    <div className="text-sm  text-gray-800">
                      {data.contract.contract_no}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 border border-gray-200">
                    <div className="text-sm font-bold text-gray-600 tracking-wide mb-1">
                      Date
                    </div>
                    <div className="text-sm  text-gray-800">
                      {data.contract.contract_date || '-'}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 border border-gray-200">
                    <div className="text-sm  font-bold text-gray-600 tracking-wide mb-1">
                      Supplier
                    </div>
                    <div className="text-sm  text-gray-800">
                      {data.contract.supplier}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 border border-gray-200">
                    <div className="text-sm font-bold  text-gray-600 tracking-wide mb-1">
                      Material
                    </div>
                    <div className="text-sm  text-gray-800">
                      {data.contract.material}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 border border-gray-200">
                    <div className="text-sm font-bold text-gray-600 tracking-wide mb-1">
                      Origin
                    </div>
                    <div className="text-sm  text-gray-800">
                      {data.contract.origin}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 border border-gray-200">
                    <div className="text-sm font-bold text-gray-600 tracking-wide mb-1">
                      Qty (MT)
                    </div>
                    <div className="text-sm  text-gray-800">
                      {data.contract.total_qty_mt}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 border border-gray-200">
                    <div className="text-sm font-bold text-gray-600 tracking-wide mb-1">
                      Unit Price (USD)
                    </div>
                    <div className="text-sm  text-gray-800">
                      {data.contract.unit_price_usd ? parseFloat(data.contract.unit_price_usd).toFixed(2) : '-'}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 border border-gray-200">
                    <div className="text-sm font-bold text-gray-600 tracking-wide mb-1">
                      Incoterm
                    </div>
                    <div className="text-sm  text-gray-800">
                      {data.contract.incoterm || '-'}
                    </div>
                  </div>
                </div>
                </div>
              </section>
            )}

            {/* Import Orders Section (Accordion) */}
            {data.importOrders && data.importOrders.length > 0 && (
              <div id="import-orders" className={`transition-all duration-700 ease-in-out delay-200 ${showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
                {data.importOrders.map((order, idx) => {
                  // Use vessel information date (order_date) for inventory age calculation
                  const vesselInfoDate = order.order_date;
                  let age = 0;
                  if (vesselInfoDate) {
                    const vesselDateObj = new Date(vesselInfoDate);
                    if (!isNaN(vesselDateObj)) {
                      const today = new Date();
                      age = Math.max(0, Math.floor((today - vesselDateObj) / (1000 * 60 * 60 * 24)));
                    }
                  }
                  return (
                    <div key={order.order_id}>
                      {/* Show prominent Age line above vessel info section, left-aligned */}
                      {vesselInfoDate && age > 0 && (
                        <div className="mb-2">
                          <span className="bg-gray-100 text-gray-800 px-3 py-1 font-bold text-sm">
                           Inventory Age: {age} Days
                          </span>
                        </div>
                      )}
                      <section className={sectionClass}>
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-lg font-bold text-gray-800">
                            Vessel Information
                          </h3>
                        </div>
                        <div className="border border-gray-200 overflow-x-auto">
                          <table className="min-w-max w-full">
                            <thead>
                              <tr>
                                <th className={thClass}>Id</th>
                                <th className={thClass}>Vessel Name</th>
                                <th className={thClass}>Date</th>
                                <th className={thClass}>Total Quantity (MT)</th>
                                <th className={thClass}>Total BLs</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white">
                              <tr
                                className="cursor-pointer  hover:bg-gray-100 transition-colors border-b border-gray-200 bg-gray-50"
                                onClick={() => toggleOrder(order.order_id)}
                              >
                                <td className={tdClass}>{order.order_id}</td>
                                <td className={tdClass}>
                                  {[
                                    ...new Set(
                                      (data.billsOfLading || [])
                                        .filter(
                                          (bl) => bl.order_id === order.order_id
                                        )
                                        .map((bl) => bl.vessel_name)
                                    ),
                                  ].join(", ")}
                                </td>
                                <td className={tdClass}>
                                  {formatDateDDMMYY(order.order_date)}
                                </td>
                                <td className={tdClass}>{order.quantity_mt}</td>
                                <td className={tdClass}>
                                  {
                                    (data.billsOfLading || []).filter(
                                      (bl) => bl.order_id === order.order_id
                                    ).length
                                  }
                                </td>
                              </tr>
                              {expandedOrders[order.order_id] && (
                                <tr>
                                  <td colSpan={6} className="px-4 py-3 bg-gray-50">
                                    {/* BLs */}
                                    <div className="font-bold text-gray-700 mb-2 text-sm">
                                      Bills of Lading
                                    </div>
                                    {(data.billsOfLading || []).filter(
                                      (bl) => bl.order_id === order.order_id
                                    ).length > 0 ? (
                                      <table className="min-w-max w-full text-sm border mb-2">
                                        <thead className="bg-white border-b text-gray-600 font-semibold">
                                          <tr>
                                            {Object.keys(
                                              data.billsOfLading.filter(
                                                (bl) => bl.order_id === order.order_id
                                              )[0] || {}
                                            )
                                              .filter(key => key !== 'order_id' && key !== 'bl_id')
                                              .map((key) => (
                                                <th
                                                  key={key}
                                                  className="px-2 py-1 text-left"
                                                >
                                                  {key.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                                </th>
                                              ))}
                                            {/* Add Departure Date column if not present */}
                                            {!Object.keys(
                                              data.billsOfLading.filter(
                                                (bl) => bl.order_id === order.order_id
                                              )[0] || {}
                                            ).includes('departure_date') && (
                                              <th className="px-2 py-1 text-left">Departure Date</th>
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {data.billsOfLading
                                            .filter(
                                              (bl) => bl.order_id === order.order_id
                                            )
                                            .map((bl) => (
                                              <tr
                                                key={bl.bl_id || bl.id}
                                                className="border-b hover:bg-gray-50 bg-blue-50 font-normal"
                                              >
                                                {Object.entries(bl)
                                                  .filter(([key]) => key !== 'order_id' && key !== 'bl_id')
                                                  .map(([key, val], idx) => (
                                                    <td key={idx} className="px-2 py-1">
                                                      {typeof val === "string" &&
                                                      val.match(/\d{4}-\d{2}-\d{2}T/)
                                                        ? formatDateDDMMYY(val)
                                                        : String(val)}
                                                    </td>
                                                  ))}
                                                {/* Add Departure Date cell if not present */}
                                                {!('departure_date' in bl) && (
                                                  <td className="px-2 py-1">{formatDateDDMMYY(bl.departure_date || bl.latest_departure_date)}</td>
                                                )}
                                              </tr>
                                            ))}
                                        </tbody>
                                      </table>
                                    ) : (
                                      <div className="text-xs text-gray-500 bg-blue-50 py-2 px-2 rounded">
                                        No BL found.
                                      </div>
                                    )}
                                    {/* Customs Clearance */}
                                    <div className="font-bold text-gray-700 mb-2 text-sm">
                                      Custom Clearance
                                    </div>
                                    {(() => {
                                      const customsForThisOrder = (data.customsClearance || []).filter(
                                        (cc) => String(cc.order_id) === String(order.order_id)
                                      );
                                      console.log(`Customs clearance for order ${order.order_id}:`, customsForThisOrder);
                                      return customsForThisOrder.length > 0;
                                    })() ? (
                                      <table className="min-w-max w-full text-sm border mb-2">
                                        <thead className="bg-white border-b text-gray-600 font-semibold">
                                          <tr>
                                            <th className="px-2 py-1 text-left">Documents Release Date</th>
                                            <th className="px-2 py-1 text-left">Documents Handover Date</th>
                                            <th className="px-2 py-1 text-left">Clearance Date</th>
                                            <th className="px-2 py-1 text-left">PQ Status</th>
                                            <th className="px-2 py-1 text-left">FSSAI Status</th>
                                            <th className="px-2 py-1 text-left">Bill of Entry No</th>
                                            <th className="px-2 py-1 text-left">Bill of Entry Date</th>
                                            <th className="px-2 py-1 text-left">Duty Paid USD</th>
                                            <th className="px-2 py-1 text-left">Demurrage Amount</th>
                                            <th className="px-2 py-1 text-left">Remarks</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {data.customsClearance
                                            .filter(
                                              (cc) => String(cc.order_id) === String(order.order_id)
                                            )
                                            .map((cc) => (
                                              <tr
                                                key={`${cc.order_id}-${cc.bill_of_entry_no || 'no-entry'}`}
                                                className="border-b hover:bg-gray-50 bg-blue-50 font-normal"
                                              >
                                                <td className="px-2 py-1">
                                                  {formatDateDDMMYY(cc.documents_release_date)}
                                                </td>
                                                <td className="px-2 py-1">
                                                  {formatDateDDMMYY(cc.documents_handover_date)}
                                                </td>
                                                <td className="px-2 py-1">
                                                  {formatDateDDMMYY(cc.clearance_date)}
                                                </td>
                                                <td className="px-2 py-1">
                                                  {cc.pq_status || '-'}
                                                </td>
                                                <td className="px-2 py-1">
                                                  {cc.fssai_status || '-'}
                                                </td>
                                                <td className="px-2 py-1">
                                                  {cc.bill_of_entry_no || '-'}
                                                </td>
                                                <td className="px-2 py-1">
                                                  {formatDateDDMMYY(cc.bill_of_entry_date)}
                                                </td>
                                                <td className="px-2 py-1">
                                                  {cc.duty_paid_usd ? parseFloat(cc.duty_paid_usd).toFixed(2) : '-'}
                                                </td>
                                                <td className="px-2 py-1">
                                                  {cc.demurrage_amount ? parseFloat(cc.demurrage_amount).toFixed(2) : '-'}
                                                </td>
                                                <td className="px-2 py-1">
                                                  {cc.remarks || '-'}
                                                </td>
                                              </tr>
                                            ))}
                                        </tbody>
                                      </table>
                                    ) : (
                                      <div className="text-xs text-gray-500 bg-blue-50 py-2 px-2 rounded">
                                        No customs clearance found.
                                      </div>
                                    )}
                                    {/* Customs Clearance Totals */}
                                    <div className="font-bold text-gray-700 mt-3 p-2 bg-gray-100 border border-gray-200">
                                      Custom Clearance: 
                                      <span className="font-normal ml-2 text-[] text-sm">
                                        {(() => {
                                          const customsForOrder = (data.customsClearance || []).filter(cc => String(cc.order_id) === String(order.order_id));
                                          const totalDuty = customsForOrder.reduce((sum, cc) => sum + (parseFloat(cc.duty_paid_usd) || 0), 0);
                                          const totalDemurrage = customsForOrder.reduce((sum, cc) => sum + (parseFloat(cc.demurrage_amount) || 0), 0);
                                          const subtotal = totalDuty + totalDemurrage;
                                          return subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                        })()}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </section>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Insurance Report Section */}
            {data.insuranceReport && data.insuranceReport.length > 0 && (
              <section id="insurance" className={`${sectionClass} transition-all duration-700 ease-in-out delay-300 ${showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-lg font-bold text-gray-800">
                   Marine Insurance Requested
                  </h3>
                </div>
                <div className="border border-gray-200 overflow-x-auto">
                  <table className="min-w-max w-full">
                                          <thead className="bg-gray-100">
                      <tr>
                          
                        <th className={thClass}>Buyer</th>
                        <th className={thClass}>State</th>
                        <th className={thClass}>Loading Port</th>
                        <th className={thClass}>Destination Port</th>
                        <th className={thClass}>Quantity</th>
                        <th className={thClass}>Rate</th>
                        <th className={thClass}>Amount USD</th>
                        <th className={thClass}>Amount INR</th>
                        <th className={thClass}>USD INR Product</th>
                        <th className={thClass}>Markup %</th>
                        <th className={thClass}>Markup</th>
                        <th className={thClass}>Total Value</th>
                        <th className={thClass}>Insurance Premium</th>
                        <th className={thClass}>GST</th>
                        <th className={thClass}>Total Premium</th>
                        <th className={thClass}>GST No</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.insuranceReport.map((row, i) => (
                        <tr key={i} className={zebraClass(i) + " hover:bg-blue-50 transition-colors"}>
                          
                        <td className={tdClass}>{row.buyer_business_name || row.buyer_id}</td>
                        <td className={tdClass}>{row.state || '-'}</td>
                        <td className={tdClass}>{row.loading_port || '-'}</td>
                        <td className={tdClass}>{row.destination_port || '-'}</td>
                        <td className={tdClass}>{row.quantity}</td>
                        <td className={tdClass}>{row.rate}</td>
                        <td className={tdClass}>{row.amount_usd}</td>
                        <td className={tdClass}>{row.amount_inr}</td>
                        <td className={tdClass}>{row.usd_inr_product}</td>
                        <td className={tdClass}>{row.markup_percent}</td>
                        <td className={tdClass}>{row.markup}</td>
                        <td className={tdClass}>{row.total_value}</td>
                        <td className={tdClass}>{row.insurance_premium}</td>
                        <td className={tdClass}>{row.gst}</td>
                        <td className={tdClass}>{row.total_premium}</td>
                        <td className={tdClass}>{row.gst_no}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Sales Section with Tabs */}
          {((data.salesContracts && data.salesContracts.length > 0) || 
            (data.deliveryOrders && data.deliveryOrders.length > 0) || 
            (data.salesOrders && data.salesOrders.length > 0)) && (
            <section id="sales-contracts" className={`${sectionClass} transition-all duration-700 ease-in-out delay-400 ${showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-bold text-gray-800">
                  Sales Section
                </h3>
              </div>
              
              {/* Tab Navigation */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveSalesTab('sales-contracts')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeSalesTab === 'sales-contracts'
                        ? 'border-[#2691cc] text-[#2691cc]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Sales Contracts ({data.salesContracts.length})
                  </button>
                  <button
                    onClick={() => setActiveSalesTab('delivery-orders')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeSalesTab === 'delivery-orders'
                        ? 'border-[#2691cc] text-[#2691cc]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Delivery Orders ({(data.deliveryOrders || []).length})
                  </button>
                  <button
                    onClick={() => setActiveSalesTab('sales-orders')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeSalesTab === 'sales-orders'
                        ? 'border-[#2691cc] text-[#2691cc]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Sales Orders ({(data.salesOrders || []).length})
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="border border-gray-200 overflow-x-auto">
                {/* Sales Contracts Tab */}
                {activeSalesTab === 'sales-contracts' && (
                  <>
                    {data.salesContracts && data.salesContracts.length > 0 ? (
                      <table className="min-w-max w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className={thClass}>Sales Contract No</th>
                            <th className={thClass}>Qty (MT)</th>
                            <th className={thClass}>Buyer</th>
                            <th className={thClass}>Delivery Point</th>
                            <th className={thClass}>Date</th>
                            <th className={thClass}>Lifting Start Date</th>
                            <th className={thClass}>Lifting End Date</th>
                            <th className={thClass}>Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {data.salesContracts.map((sc, idx) => (
                            <tr
                              key={sc.sales_contract_id}
                              className="hover:bg-gray-100 bg-gray-50 text-sm"
                            >
                              <td className="px-3 py-2">{sc.sales_contract_no}</td>
                              <td className="px-3 py-2">{sc.quantity_mt}</td>
                              <td className="px-3 py-2">
                                {sc.domestic_buyer_name}
                              </td>
                              <td className="px-3 py-2">
                                {sc.delivery_point_warehouse_name || sc.warehouse_location || '-'}
                              </td>
                              <td className="px-3 py-2">
                                {formatDateDDMMYY(sc.contract_date)}
                              </td>
                              <td className="px-3 py-2">
                                {formatDateDDMMYY(sc.delivery_start_date)}
                              </td>
                              <td className="px-3 py-2">
                                {formatDateDDMMYY(sc.delivery_end_date)}
                              </td>
                              <td className="px-3 py-2">
                                <button
                                  onClick={() => handleContractClick(sc.sales_contract_id)}
                                  className="text-[#2691cc] hover:text-[] transition-colors p-1 rounded hover:bg-blue-50"
                                  title="View Delivery Orders for this contract"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        No sales contracts found for this purchase contract.
                      </div>
                    )}
                  </>
                )}

                {/* Delivery Orders Tab */}
                {activeSalesTab === 'delivery-orders' && (
                  <>
                    {/* Filter Indicator */}
                    {selectedContractFilter && (
                      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <svg className="w-5 h-5 text-[#2691cc] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                            </svg>
                            <span className="text-[#2691cc] font-medium">Filtered by Contract: </span>
                            <span className="ml-2 text-[#2691cc] font-semibold">
                              {(data.salesContracts || []).find(sc => sc.sales_contract_id === selectedContractFilter)?.sales_contract_no || selectedContractFilter}
                            </span>
                          </div>
                          <button
                            onClick={clearContractFilter}
                            className="px-3 py-1 bg-[#2691cc] text-white text-sm rounded hover:bg-[] transition-colors flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Clear Filter
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <table className="min-w-max w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className={thClass}>DO ID</th>
                          <th className={thClass}>DO Date</th>
                          <th className={thClass}>DO Number</th>
                          <th className={thClass}>Sales Contract No</th>
                          <th className={thClass}>Issuing Authority</th>
                          <th className={thClass}>Buyer Name</th>
                          <th className={thClass}>Commodity</th>
                          <th className={thClass + " text-right"}>Quantity (MT)</th>
                          <th className={thClass}>Warehouse</th>
                          <th className={thClass}>Warehouse Location</th>
                          <th className={thClass}>CMA/CHA Type</th>
                          <th className={thClass}>CMA/CHA Name</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {(data.deliveryOrders || [])
                          .filter(dOrder => !selectedContractFilter || dOrder.sales_contract_id === selectedContractFilter)
                          .map((dOrder, idx) => {
                            // Find the sales contract number by matching sales_contract_id
                            const salesContract = (data.salesContracts || []).find(
                              sc => sc.sales_contract_id === dOrder.sales_contract_id
                            );
                            const salesContractNo = salesContract ? salesContract.sales_contract_no : '-';
                            
                            return (
                              <tr
                                key={dOrder.do_id}
                                className="hover:bg-gray-50 text-sm"
                              >
                                <td className="px-3 py-2">{dOrder.do_id}</td>
                                <td className="px-3 py-2">{formatDateDDMMYY(dOrder.do_date)}</td>
                                <td className="px-3 py-2">{dOrder.do_number}</td>
                                <td className="px-3 py-2 font-medium text-[#2691cc]">{salesContractNo}</td>
                                <td className="px-3 py-2">{dOrder.issuing_authority}</td>
                                <td className="px-3 py-2">{dOrder.buyer_name}</td>
                                <td className="px-3 py-2">{dOrder.commodity_name}</td>
                                <td className="px-3 py-2 text-right">{dOrder.quantity_mt}</td>
                                <td className="px-3 py-2">{dOrder.warehouse_name}</td>
                                <td className="px-3 py-2">{dOrder.warehouse_location}</td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    dOrder.check_cma_cha === 'CMA' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                  }`}>
                                    {dOrder.check_cma_cha}
                                  </span>
                                </td>
                                <td className="px-3 py-2">{dOrder.cma_name}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </>
                )}

                {/* Sales Orders Tab */}
                {activeSalesTab === 'sales-orders' && (
                  <>
                    {data.salesOrders && data.salesOrders.length > 0 ? (
                      <table className="min-w-max w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className={thClass}>Sales Contract No</th>
                            <th className={thClass}>Order Date</th>
                            <th className={thClass}>Buyer</th>
                            <th className={thClass}>Warehouse</th>
                            <th className={thClass}>CMA</th>
                            <th className={thClass}>Commodity</th>
                            <th className={thClass + " text-right"}>Quantity (MT)</th>
                            <th className={thClass + " text-right"}>Unit Price</th>
                            <th className={thClass}>Broker</th>
                            <th className={thClass}>Remarks</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {data.salesOrders.map((so, idx) => {
                            // Find the sales contract number by matching sales_contract_id
                            const salesContract = (data.salesContracts || []).find(
                              sc => sc.sales_contract_id === so.sales_contract_id
                            );
                            const salesContractNo = salesContract ? salesContract.sales_contract_no : '-';
                            
                            return (
                              <tr
                                key={so.sale_id}
                                className="text-sm hover:bg-gray-50"
                              >
                                <td className="px-3 py-2">{salesContractNo}</td>
                                <td className="px-3 py-2">{formatDateDDMMYY(so.order_date)}</td>
                                <td className="px-3 py-2">{so.domestic_buyer_name}</td>
                                <td className="px-3 py-2">{so.warehouse_name}</td>
                                <td className="px-3 py-2">{so.cma_name}</td>
                                <td className="px-3 py-2">{so.commodity_name}</td>
                                <td className="px-3 py-2 text-right">{so.quantity_mt}</td>
                                <td className="px-3 py-2 text-right">{so.sale_price_per_mt}</td>
                                <td className="px-3 py-2">{so.broker_name}</td>
                                <td className="px-3 py-2">{so.remarks}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        No sales orders found for this purchase contract.
                      </div>
                    )}
                  </>
                )}


              </div>
            </section>
          )}

       

        {/* Warehouse Summary Cards */}
        {data.warehouseStockFlow && data.warehouseStockFlow.length > 0 && (
          <section id="warehouse" className={`${sectionClass} transition-all duration-700 ease-in-out delay-500 ${showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
                          <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-bold text-gray-800">
                  Warehouse Status
                </h3>
                <div className="bg-blue-50 rounded-full px-2 py-1 text-lg font-bold text-gray-800">
                   {(() => {
                      // Group by warehouse and calculate individual balances, then sum them up
                      const warehouseGroups = {};
                      data.warehouseStockFlow.forEach(row => {
                        const key = `${row.warehouse_name}-${row.warehouse_location}`;
                        if (!warehouseGroups[key]) {
                          warehouseGroups[key] = {
                            warehouse_name: row.warehouse_name,
                            warehouse_location: row.warehouse_location,
                            transactions: []
                          };
                        }
                        warehouseGroups[key].transactions.push(row);
                      });

                      // Calculate balance for each warehouse and sum them up
                      const totalBalance = Object.values(warehouseGroups).reduce((total, warehouse) => {
                        const inwardTotal = warehouse.transactions
                          .filter(t => t.type === 'Inward' || t.type === 'Transfer In')
                          .reduce((sum, t) => sum + (Number(t.quantity) || 0), 0);
                        
                        const outwardTotal = warehouse.transactions
                          .filter(t => t.type === 'Outward' || t.type === 'Transfer Out')
                          .reduce((sum, t) => sum + (Number(t.quantity) || 0), 0);

                        const warehouseBalance = inwardTotal - outwardTotal;
                        return total + warehouseBalance;
                      }, 0);
                      
                      return totalBalance.toFixed(3);
                    })()} MT
                </div>
                <button 
                  onClick={toggleWarehouseStockFlow}
                  className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Toggle Warehouse Stock Flow"
                >
                  <svg 
                    className={`w-5 h-5 text-gray-600 transition-transform ${showWarehouseStockFlow ? 'rotate-45' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M12 4v16m8-8H4" 
                    />
                  </svg>
                </button>
              </div>
            
            {/* Warehouse Summary Cards */}
            <div className="overflow-x-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 min-w-max">
              {(() => {
                // Group by warehouse
                const warehouseGroups = {};
                data.warehouseStockFlow.forEach(row => {
                  const key = `${row.warehouse_name}-${row.warehouse_location}`;
                  if (!warehouseGroups[key]) {
                    warehouseGroups[key] = {
                      warehouse_name: row.warehouse_name,
                      warehouse_location: row.warehouse_location,
                      warehouse_type: row.warehouse_type,
                      vessel_name: row.vessel_name,
                      commodity: row.commodity,
                      transactions: []
                    };
                  }
                  warehouseGroups[key].transactions.push(row);
                });

                return Object.values(warehouseGroups).map((warehouse, idx) => {
                  const inwardTotal = warehouse.transactions
                    .filter(t => t.type === 'Inward' || t.type === 'Transfer In')
                    .reduce((sum, t) => sum + (Number(t.quantity) || 0), 0);
                  
                  const outwardTotal = warehouse.transactions
                    .filter(t => t.type === 'Outward' || t.type === 'Transfer Out')
                    .reduce((sum, t) => sum + (Number(t.quantity) || 0), 0);

                  const netStock = inwardTotal - outwardTotal;

                  return (
                    <div key={idx} className="bg-white border border-gray-200 p-2">
                      {/* Warehouse Header */}
                      <div className="mb-1 pb-1 border-b border-gray-100">
                        <h4 className="text-md font-bold text-gray-800 mb-2">
                          {warehouse.warehouse_name}
                        </h4>
                        <div className="text-sm text-gray-700  space-y-1.5">
                          <div className="flex justify-between"><span className="font-bold text-gray-700">Location:</span> <span className="text-right">{warehouse.warehouse_location}</span></div>
                          <div className="flex justify-between"><span className="font-bold text-gray-700">Type:</span> <span className="text-right">{warehouse.warehouse_type}</span></div>
                          <div className="flex justify-between"><span className="font-bold text-gray-700">Vessel:</span> <span className="text-right">{warehouse.vessel_name}</span></div>
                          <div className="flex justify-between"><span className="font-bold text-gray-700">Commodity:</span> <span className="text-right">{warehouse.commodity}</span></div>
                        </div>
                      </div>

                      {/* Stock Summary */}
                      <div className="mt-1">
                        <div className="text-sm text-gray-700 flex justify-between">
                          <span className="font-bold text-gray-700">Balance Qty:</span> <span className="text-right">{netStock.toFixed(3)} MT</span>
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
              </div>
            </div>
            
          {data.warehouseStockFlow && data.warehouseStockFlow.length > 0 && (
            <section className={sectionClass + " overflow-hidden mt-2"}>
              <div 
                className={`transition-all duration-500 ease-in-out ${
                  showWarehouseStockFlow 
                    ? 'max-h-screen opacity-100' 
                    : 'max-h-0 opacity-0'
                }`}
              >
              
                
                  <table className="min-w-max w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className={thClass}>Warehouse Name</th>
                        <th className={thClass}>Warehouse Location</th>
                        <th className={thClass}>Warehouse Type</th>
                        <th className={thClass}>Vessel Name</th>
                        <th className={thClass}>Commodity</th>
                        <th className={thClass}>Date</th>
                        <th className={thClass}>Type</th>
                        <th className={thClass + " text-center"}>Inward Qty (MT)</th>
                        <th className={thClass + " text-center"}>Outward Qty (MT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.warehouseStockFlow.map((row, i) => (
                        <tr
                          key={i}
                          className={
                            zebraClass(i) +
                            " hover:bg-blue-50 transition-colors"
                          }
                        >
                          <td className={tdClass}>{row.warehouse_name}</td>
                          <td className={tdClass}>{row.warehouse_location}</td>
                          <td className={tdClass}>{row.warehouse_type}</td>
                          <td className={tdClass}>{row.vessel_name}</td>
                          <td className={tdClass}>{row.commodity}</td>
                          <td className={tdClass}>{formatDateDDMMYY(row.date)}</td>
                          <td className={tdClass}>{row.type}</td>
                          <td className={tdClass + " text-center font-semibold"}>
                            {(row.type === 'Inward' || row.type === 'Transfer In') ? 
                              Number(row.quantity).toFixed(3) : '-'}
                          </td>
                          <td className={tdClass + " text-center font-semibold"}>
                            {(row.type === 'Outward' || row.type === 'Transfer Out') ? 
                              Number(row.quantity).toFixed(3) : '-'}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-bold">
                        <td colSpan="7" className={tdClass + " text-right"}>
                          Subtotal:
                        </td>
                        <td className={tdClass + " text-center"}>
                          {data.warehouseStockFlow
                            .filter(row => row.type === 'Inward' || row.type === 'Transfer In')
                            .reduce((sum, row) => sum + (Number(row.quantity) || 0), 0)
                            .toFixed(3)}
                        </td>
                        <td className={tdClass + " text-center"}>
                          {data.warehouseStockFlow
                            .filter(row => row.type === 'Outward' || row.type === 'Transfer Out')
                            .reduce((sum, row) => sum + (Number(row.quantity) || 0), 0)
                            .toFixed(3)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                
              </div>
            </section>
          )} 
          </section>
        )}
         
        {/* Cost Breakup Section */}
        {data.costBreakups && data.costBreakups.length > 0 && (
          <section id="cost-breakup" className={`${sectionClass} transition-all duration-700 ease-in-out delay-600 ${showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
                          <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-bold text-gray-800">
                  Cost Breakup
                </h3>
              </div>
            <div className="border border-gray-200 overflow-x-auto">
              <table className="min-w-max w-full">
                                  <thead className="bg-gray-100">
                  <tr>
                    <th className={thClass}>Invoice No</th>
                    <th className={thClass}>Component Code</th>
                    <th className={thClass + " text-right"}>
                      Amount (INR)
                    </th>
                    <th className={thClass}>Currency</th>
                    <th className={thClass + " text-right"}>FX Rate</th>
                    
                    <th className={thClass}>Remarks</th>
                    <th className={thClass}>Component Type</th>
                  </tr>
                </thead>
                <tbody>
                  {data.costBreakups.map((row, i) => (
                    <tr
                      key={i}
                      className={
                        zebraClass(i) +
                        " hover:bg-blue-50 transition-colors"
                      }
                    >
                      <td className={tdClass}>{row.order_id}</td>
                      <td className={tdClass}>{row.component_code}</td>
                      <td className={tdClass + " text-right"}>
                        {row.amount_inr}
                      </td>
                      <td className={tdClass}>{row.currency}</td>
                      <td className={tdClass + " text-right"}>
                        {row.fx_rate}
                      </td>
                      
                      <td className={tdClass}>{row.remarks}</td>
                      <td className={tdClass}>{row.component_type}</td>
                    </tr>
                  ))}
                  {/* Subtotal Row */}
                  <tr className="bg-blue-100 font-bold">
                    <td className={tdClass} colSpan={2}>
                      Subtotal
                    </td>
                    <td className={tdClass + " text-right"}>
                      {data.costBreakups
                        .reduce(
                          (sum, row) =>
                            sum + (parseFloat(row.amount_inr) || 0),
                          0
                        )
                        .toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </td>
                    <td className={tdClass}></td>
                    <td className={tdClass}></td>
                    <td className={tdClass}></td>
                    <td className={tdClass}></td>
                    <td className={tdClass}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}
        </div>
      ) : (
        <div className={`flex flex-col items-center justify-center py-8 space-y-2 transition-all duration-700 ease-in-out ${showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
          <svg className="h-8 w-8 text-gray-200 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
          </svg>
          <h3 className="text-base font-semibold text-gray-500">No records found</h3>
          <p className="text-gray-400 text-sm">Please search for a valid Purchase Contract Number.</p>
        </div>
      )}
    </div>
  );
};

export default MasterReport;

